/**
 * @file PageSearchGroup.tsx
 * @description Группа «Для поиска» в «Настройках страницы» конструктора
 * (phase19.16 Р1-6а → Р3в, v0.19.64; SB44, SEO11, SEO12): заголовок и
 * описание для поиска с образцом «Так в поиске», «Не показывать страницу в
 * поиске» и «Ключевые запросы». Вынесена из `PageSettingsPanel` (компонент ≤
 * 200 строк) с общим контрактом формы панели.
 *
 * Окончание заголовков и описание сайта для образца — из общих SEO-настроек
 * магазина (с Р3в не часть черновика вида сайта; SB145); без права их
 * читать — образец без окончания. Ключевые запросы — заметка, сохраняются
 * той же кнопкой «Сохранить», но сразу, вне публикации (подпись под полем).
 *
 * «SEO-техника» v0.19.65 (SB146; uiux-seo-tech §3.4): после «Не показывать
 * страницу в поиске» — поле «Хлебные крошки» (`PageBreadcrumbsField`); к
 * сводке группы — «· крошки скрыты/показаны», когда выбор не «как на сайте».
 */

import { useMemo } from "react";

import { SettingsGroup } from "../../ui/settings-groups";
import { CheckRow } from "../../site-view/CheckRow";
import { TextField } from "../../site-view/TextField";
import { SeoKeywordsField } from "../../site-seo/SeoKeywordsField";
import { PageBreadcrumbsField } from "./PageBreadcrumbsField";
import { useHasPermission } from "../../../auth/hooks";
import type { SitePageDetail } from "../../../lib/api-site-pages";
import { PAGE_SEO_DESCRIPTION_MAX, PAGE_SEO_DESCRIPTION_SOFT, PAGE_SEO_TITLE_SOFT, type PageForm, type PageFormErrors } from "../../../lib/site-builder/page-form";
import { pageBreadcrumbsSummary } from "../../../lib/site-builder/page-breadcrumbs";
import { pageH1Texts } from "../../../lib/site-builder/page-h1";
import type { SeoKeywordsValue } from "../../../lib/site-seo/keywords";
import { useShopSeoSnapshot } from "../../../lib/site-seo/seo-settings-store";
import { titleWithSuffix } from "../../../lib/site-view/seo";

interface Props {
  page: SitePageDetail;
  form: PageForm;
  patch: (next: Partial<PageForm>) => void;
  errors: PageFormErrors;
  keywords: SeoKeywordsValue;
  onKeywords: (value: SeoKeywordsValue) => void;
  readOnly: boolean;
}

/** Подпись под «Ключевыми запросами» страницы (SEO11, решение 8 db-design-seo-settings). */
export const PAGE_KEYWORDS_NOTE = "Заметка для вас: на сайт не выводится. Сохраняются кнопкой «Сохранить», без публикации страницы.";

export function PageSearchGroup({ page, form, patch, errors, keywords, onKeywords, readOnly }: Props) {
  // Читать общие SEO-настройки можно правом контента или настроек (контракт r3v §0).
  const canSettings = useHasPermission("SHOP_MANAGE_SETTINGS");
  const canContent = useHasPermission("SHOP_MANAGE_CONTENT");
  const canReadSeo = canSettings || canContent;
  const seo = useShopSeoSnapshot(canReadSeo);
  const searchTitle = titleWithSuffix(form.seoTitle.trim() || form.title, seo?.titleSuffix ?? null);
  const h1 = useMemo(() => pageH1Texts(page.blocks), [page.blocks]);
  const lock = readOnly ? ("readonly" as const) : undefined;
  const summary = `${form.seoTitle.trim() ? "заголовок задан" : "заголовок по названию"}${form.noIndex ? " · скрыта из поиска" : ""}${pageBreadcrumbsSummary(form.breadcrumbs)}`;

  return (
    <SettingsGroup id="seo" title="Для поиска" tocLabel="Поиск" summary={summary} status={errors.seoTitle || errors.seoDescription ? "error" : lock}>
      <TextField label="Заголовок в поиске" value={form.seoTitle} placeholder={form.title} softMax={PAGE_SEO_TITLE_SOFT} softText="Поиск покажет первые ~60 знаков." error={errors.seoTitle} disabled={readOnly} hint="Пусто — название страницы и окончание из «Сайт → SEO»." onChange={(seoTitle) => patch({ seoTitle })} />
      <TextField label="Описание в поиске" value={form.seoDescription} multiline max={PAGE_SEO_DESCRIPTION_MAX} softMax={PAGE_SEO_DESCRIPTION_SOFT} softText="Поиск покажет первые ~160 знаков." error={errors.seoDescription} disabled={readOnly} hint="Пусто — описание сайта по умолчанию из «Сайт → SEO»." onChange={(seoDescription) => patch({ seoDescription })} />
      <div className="flex flex-col gap-1 rounded-xl bg-surface-low p-4" aria-label="Так в поиске">
        <span className="type-caption text-on-surface-variant">Так в поиске</span>
        <span className="type-body font-medium text-info">{searchTitle}</span>
        <span className="type-caption line-clamp-2 text-on-surface">
          {form.seoDescription.slice(0, PAGE_SEO_DESCRIPTION_SOFT) || seo?.description || "Описание не задано — поисковик возьмёт текст со страницы."}
        </span>
      </div>
      <CheckRow checked={form.noIndex} disabled={readOnly} onChange={(noIndex) => patch({ noIndex })}>
        Не показывать страницу в поиске
      </CheckRow>
      <PageBreadcrumbsField value={form.breadcrumbs} onChange={(breadcrumbs) => patch({ breadcrumbs })} home={page.kind === "SYSTEM_HOME"} disabled={readOnly} />
      <SeoKeywordsField value={keywords} onChange={onKeywords} title={searchTitle} h1={h1} conflicts={page.seoKeywordConflicts ?? []} disabled={readOnly} note={PAGE_KEYWORDS_NOTE} />
    </SettingsGroup>
  );
}
